import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  InputBase,
  Fade,
} from "@mui/material";

import { Search, Menu, X, ChevronDown } from "lucide-react";

import { useNavigate, useLocation } from "react-router-dom";

import muzukurulogo from "../../../../assets/muzukurublack.png";

interface MenuItemProps {
  title: string;
  href: string;
  hasDropdown?: boolean;
}

const menuItems: MenuItemProps[] = [
  { href: "/", title: "Home" },
  { href: "/review", title: "Review" },
  { href: "/pricing", title: "Pricing" },
  { href: "/resources", title: "Resources" },
  { href: "/business", title: "Business" },
  { href: "/contact", title: "Contact" },
  { href: "/death-notices", title: "Death Notices", hasDropdown: true },
  { href: "/post-funeral", title: "Post a Funeral" },
  { href: "/socials", title: "Socials" },
];

const MainNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const pathname = location.pathname;

  const [, setMobileMenuOpen] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [showLogin, setShowLogin] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname;

    const isMuzukuruDomain =
      hostname === "muzukuru.com" || hostname === "www.muzukuru.com";

    setShowLogin(!isMuzukuruDomain);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [searchOpen]);

  const handleSearchToggle = () => {
    setSearchOpen((prev) => !prev);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleNavigate = (href: string) => {
    navigate(href);
    setMobileMenuOpen(false);
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmed = searchTerm.trim();

    if (!trimmed) return;

    console.log("Searching:", trimmed);

    // navigate(`/search?q=${trimmed}`);
  };

  return (
    <>
      {/* MAIN NAVBAR */}
      <Box
        sx={{
          bgcolor: "rgba(255,255,255,.95)",

          backdropFilter: "blur(12px)",

          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1400px",

            mx: "auto",

            px: {
              xs: 2,
              md: 4,
            },

            height: 88,

            display: "flex",

            alignItems: "center",

            justifyContent: "space-between",

            gap: 3,
          }}
        >
          {/* LOGO */}
          <Box
            component="img"
            src={muzukurulogo}
            alt="Muzukuru"
            onClick={() => navigate("/")}
            sx={{
              width: 160,
              cursor: "pointer",
            }}
          />

          {/* RIGHT SIDE */}
          <Box
            sx={{
              position: "relative",

              flex: 1,

              display: "flex",

              justifyContent: "flex-end",

              minWidth: 0,
            }}
          >
            {/* NORMAL NAV */}
            <Fade in={!searchOpen} timeout={250}>
              <Box
                sx={{
                  position: "absolute",

                  right: 0,

                  display: "flex",

                  alignItems: "center",

                  gap: 3,
                }}
              >
                {/* Desktop Menu */}
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{
                    display: {
                      xs: "none",
                      lg: "flex",
                    },
                  }}
                >
                  {menuItems.map((item) => {
                    const isActive = pathname === item.href;

                    return (
                      <Box
                        key={item.title}
                        onClick={() => handleNavigate(item.href)}
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          pb: 1,
                          color: isActive ? "#7A5C48" : "#666",
                          transition: "all .2s",
                          "&:hover": {
                            color: "#7A5C48",
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                          {item.title}
                        </Typography>

                        {item.hasDropdown && <ChevronDown size={16} />}

                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            bottom: -10,
                            height: 2,
                            borderRadius: 10,
                            bgcolor: "#7A5C48",
                            width: isActive ? "100%" : 0,
                            transition: "all .3s",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>

                {/* Search */}
                <IconButton
                  onClick={handleSearchToggle}
                  sx={{
                    display: {
                      xs: "none",
                      lg: "flex",
                    },
                  }}
                >
                  <Search size={22} />
                </IconButton>

                {/* Login */}
                {showLogin && (
                  <Button
                    onClick={handleLoginClick}
                    sx={{
                      border: "1px solid rgba(0,0,0,.15)",

                      borderRadius: "999px",

                      px: 2.5,

                      py: 1,

                      color: "#000",

                      fontWeight: 700,

                      textTransform: "none",

                      "&:hover": {
                        bgcolor: "#6F4C3E",

                        color: "#fff",

                        borderColor: "#6F4C3E",
                      },
                    }}
                  >
                    Login
                  </Button>
                )}

                {/* Mobile Menu */}
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{
                    display: {
                      xs: "flex",
                      lg: "none",
                    },
                  }}
                >
                  <Menu size={28} />
                </IconButton>
              </Box>
            </Fade>

            {/* SEARCH PANEL */}
            <Fade in={searchOpen} timeout={250}>
              <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                  width: "100%",

                  maxWidth: "900px",

                  ml: "auto",

                  display: "flex",

                  alignItems: "center",

                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,

                    display: "flex",

                    alignItems: "center",

                    bgcolor: "#F8F5F2",

                    border: "1px solid rgba(122,92,72,.2)",

                    borderRadius: "999px",

                    px: 2,

                    height: 48,
                  }}
                >
                  <Search size={18} />

                  <InputBase
                    inputRef={inputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search death notices, resources, businesses..."
                    sx={{
                      ml: 1.5,
                      flex: 1,
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  sx={{
                    bgcolor: "#614438",

                    color: "#fff",

                    px: 3,

                    py: 1.5,

                    borderRadius: "999px",

                    textTransform: "none",

                    "&:hover": {
                      bgcolor: "#50362D",
                    },
                  }}
                >
                  Search
                </Button>

                <IconButton onClick={handleSearchToggle}>
                  <X size={20} />
                </IconButton>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer goes here */}
      {/* Copy your existing Drawer code below this */}
    </>
  );
};

export default MainNavbar;
